import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, ChevronDown } from "lucide-react";
import { WHATSAPP_URL } from "../lib/brand";
import { useQuotePopup } from "../context/QuotePopupContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627370778723-4d26700cd972?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxrZXJhbGElMjB0ZWElMjBwbGFudGF0aW9uJTIwbWlzdHxlbnwwfHx8fDE3ODEyNjEyMzB8MA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const { openPopup } = useQuotePopup();
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Kerala cardamom plantation in mist"
          className="w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07271E]/85 via-[#0A3A2D]/55 to-[#07271E]/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(7,39,30,0.6)_100%)]" />
      </div>

      {/* Top floating chips */}
      <div className="absolute top-24 left-0 right-0 z-10 hidden md:block">
        <div className="container-luxury flex justify-between items-center">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="chip"
          >
            <span className="w-1.5 h-1.5 bg-[#C9A227] rounded-full" /> Direct From Kerala Estates
          </motion.span>
          <motion.span
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="chip"
          >
            Bangalore B2B Wholesale
          </motion.span>
        </div>
      </div>

      {/* Content */}
      <div className="container-luxury relative z-10 pt-36 pb-24 md:pt-44 md:pb-32">
        <div className="max-w-4xl">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="heading-eyebrow"
          >
            <span className="line" />
            <span className="text">Estate-Sourced · Since 2008</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: "easeOut" }}
            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            data-testid="hero-headline"
          >
            Premium Kerala Cardamom,
            <br />
            <span className="italic font-light text-[#E5D08F]">Direct From The Source.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-8 text-white/85 text-base md:text-lg max-w-2xl font-light leading-relaxed"
            data-testid="hero-subheadline"
          >
            Supplying handpicked premium green cardamom to wholesalers, distributors,
            retailers and food businesses across Bangalore — graded, packed and delivered
            with plantation-fresh integrity.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={openPopup}
              data-testid="hero-cta-quote"
              className="btn-gold"
            >
              Get Wholesale Quote
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="btn-outline-light"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-3xl"
          >
            {[
              { k: "16+", v: "Years in Trade" },
              { k: "2.4K T", v: "Annual Volume" },
              { k: "8mm+", v: "Premium Grades" },
              { k: "300+", v: "B2B Clients" },
            ].map((s) => (
              <div key={s.v} className="border-l border-[#C9A227]/40 pl-4">
                <div className="font-serif text-[#C9A227] text-2xl md:text-3xl">
                  {s.k}
                </div>
                <div className="label-overline text-white/60 mt-1">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        onClick={() => scrollTo("why")}
        data-testid="hero-scroll-down"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 hover:text-[#C9A227] transition-colors"
      >
        <span className="label-overline">Scroll</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
